import dayjs from 'dayjs'

const LONG_DATE_FORMAT = 'MMM D, YYYY'
const TIME_FORMAT = 'h:mm A'
const TWO_DIGIT_TIME_FORMAT = 'hh:mm A'

const SECOND_MS = 1_000
const MINUTE_MS = 60 * SECOND_MS
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS
const RECENT_THRESHOLD_MS = 30 * SECOND_MS

// Month + day token that only carries the year when the date is not in the
// current year: "Aug 27" for this year, "Aug 27, 2025" for past/future years.
function monthDayToken(d: dayjs.Dayjs): string {
  return d.year() === dayjs().year() ? 'MMM D' : LONG_DATE_FORMAT
}

export function formatDate(iso: string): string {
  const d = dayjs(iso)
  if (!d.isValid()) return iso
  return d.format(monthDayToken(d))
}

export function formatLongDate(iso: string): string {
  const d = dayjs(iso)
  if (!d.isValid()) return iso
  return d.format(LONG_DATE_FORMAT)
}

export function formatTime(iso: string): string {
  const d = dayjs(iso)
  if (!d.isValid()) return ''
  return d.format(TIME_FORMAT)
}

export function splitTimestamp(iso: string): { date: string; time: string } {
  const d = dayjs(iso)
  if (!d.isValid()) return { date: iso, time: '' }
  return { date: d.format(monthDayToken(d)), time: d.format(TIME_FORMAT) }
}

export function formatNoteTimestamp(iso: string): string {
  const d = dayjs(iso)
  if (!d.isValid()) return iso
  return d.format(`${monthDayToken(d)} ${TWO_DIGIT_TIME_FORMAT}`)
}

export function formatSentAtTimestamp(iso: string): string {
  const d = dayjs(iso)
  if (!d.isValid()) return iso
  return d.format(`${monthDayToken(d)} · ${TIME_FORMAT}`)
}

// "Jan 31 at 3:45 pm", with a relative-age suffix ("(1h ago)") while the
// event is under a day old — the short history row format.
export function formatHistoryTimestamp(
  iso: string,
  now: Date = new Date(),
): string {
  const d = dayjs(iso)
  if (!d.isValid()) return iso
  const base = d.format(`${monthDayToken(d)} [at] h:mm a`)
  const diffMs = now.getTime() - d.toDate().getTime()
  if (diffMs < RECENT_THRESHOLD_MS || diffMs >= DAY_MS) return base
  if (diffMs < HOUR_MS) {
    const minutes = Math.max(1, Math.floor(diffMs / MINUTE_MS))
    return `${base} (${minutes} min ago)`
  }
  return `${base} (${Math.floor(diffMs / HOUR_MS)}h ago)`
}

export function formatRelativeTime(
  iso: string,
  now: Date = new Date(),
): string {
  const d = dayjs(iso)
  if (!d.isValid()) return iso
  const diffMs = now.getTime() - d.toDate().getTime()
  if (diffMs < RECENT_THRESHOLD_MS) return 'Just now'
  if (diffMs < HOUR_MS) {
    const minutes = Math.floor(diffMs / MINUTE_MS)
    return `${minutes} min ago`
  }
  if (diffMs < DAY_MS) {
    const hours = Math.floor(diffMs / HOUR_MS)
    return `${hours}h ago`
  }
  if (diffMs < 2 * DAY_MS) return `Yesterday, ${d.format(TIME_FORMAT)}`
  return d.format(`${monthDayToken(d)} ${TWO_DIGIT_TIME_FORMAT}`)
}
